import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { BiTrendingDown } from 'react-icons/bi'
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi'
import { MdFormatListBulleted } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'

import { Badge, IconButtonProps } from '@chakra-ui/react'
import { Box, Flex, Grid, Icon, IconButton, Image } from '@chakra-ui/react'
import styled from '@emotion/styled'

import {
  AiOperatorIcon,
  BadgeCounter,
  CaseManagementIcon,
  DarkProvider,
  DashboardIcon,
  EmergencyCallIcon,
  FacilityIcon,
  GatewayIcon,
  GlobeIcon,
  GuardTourIcon,
  IncidentIcon,
  ProgramRecommendationsIcon,
  RuleIcon,
  SecurityDeviceIcon,
  SideNavContainer,
} from '@hivewatch/dark'

import { useProgramRecommendationCountsQuery } from '@/graphql/generated/hooks'
import { selectSideNavOpen, toggleSideNavOpen } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import {
  AI_OPERATOR_HUB,
  CASE_MANAGEMENT,
  CREATE_INCIDENT_RESPONSE_WORKFLOW,
  DASHBOARD,
  DEVICES,
  EMERGENCY_CALL_LOG,
  FACILITIES,
  GATEWAYS,
  GLOBAL,
  GUARD_TOURS,
  INCIDENTS,
  INCIDENT_RULES,
  METRICS_DASHBOARD,
  PROGRAM_RECOMMENDATIONS,
  STANDARD_OPERATING_PROCEDURES,
} from '@/utils/routes'

import { SideBarEditMode } from './SideBarEditMode'
import { SideBarNavItem } from './SideBarNavItem'
import { SideBarProfile } from './SideBarProfile'

const NavToggle = styled(IconButton as React.ComponentType<IconButtonProps>)`
  position: absolute;
  right: 0;
  top: 30px;
  transform: translate(50%, -50%);
  font-size: 20px;
  height: 18px;
  min-width: 18px;
`

const NewBadge = () => {
  const { t } = useTranslation()

  return (
    <Badge
      bg='signalyellow-400'
      borderRadius='full'
      color='black'
      fontSize='10px'
      px='6px'
      py='1px'
    >
      {t('common:sidebar_new_badge')}
    </Badge>
  )
}

export const SideBar = () => {
  const dispatch = useDispatch()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const colorScheme = useLightMode()
  const {
    shouldShowDashboardPage,
    shouldShowEditMode,
    shouldShowIncidentRulesPage,
    shouldShowCaseManagementPage,
    shouldShowOperatorHubPage,
    shouldShowOperatorHub,
    shouldShowProgramRecommendationsPage,
  } = usePermissions()

  const { hasNoLiveStream, isOrangePeel } = useLiveStreamSetup()

  const aioHubKey = 'ai_operator_hub_visited'
  const [shouldShowOperatorHubBadge, setShouldShowOperatorHubBadge] =
    useState(false)

  // Used to show the New badge for OperatorHub on first time use
  useEffect(() => {
    if (shouldShowOperatorHub && typeof window !== 'undefined') {
      const visited = localStorage.getItem(aioHubKey)
      if (visited) {
        setShouldShowOperatorHubBadge(false)
      }
    }
  }, [shouldShowOperatorHub])

  const onOperatorHubClick = () => {
    trackEvent(`${BUTTON_PRESS} AI Operator Hub, Sidebar`, {
      showingNewBadge: shouldShowOperatorHubBadge,
    })
    if (shouldShowOperatorHubBadge) {
      localStorage.setItem(aioHubKey, 'true')
      setShouldShowOperatorHubBadge(false)
    }
  }

  const isRecommendationsEngineEnabled = useFeatureFlag(
    FeatureFlag.recommendationsEngine
  )
  const shouldShowLegacyDashboard = useFeatureFlag(FeatureFlag.legacyDashboard)
  const isEmergencyCallEnabled = useFeatureFlag(FeatureFlag.emergencyCall)
  const isGuardToursEnabled = useFeatureFlag(FeatureFlag.guardTours)
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isIncidentRulesEnabled = useFeatureFlag(FeatureFlag.incidentRules)
  const isIPv6RedirectionEnabled = useFeatureFlag(FeatureFlag.ipv6Redirection)
  // Edit Mode's toggle only moved to the Floor-plan header behind this flag.
  // While it's off the Side Bar keeps the toggle that governs the whole app.
  const isRemoveEditModeEnabled = useFeatureFlag(FeatureFlag.removeEditMode)

  const { data: recommendationCountsData } =
    useProgramRecommendationCountsQuery({
      skip:
        !isRecommendationsEngineEnabled ||
        !shouldShowProgramRecommendationsPage,
    })
  const apiCounts = recommendationCountsData?.programRecommendationCounts
  const pendingRecommendationsCount = apiCounts?.pending ?? 0

  return (
    <DarkProvider>
      <SideNavContainer colorScheme={colorScheme} isOpen={isSideNavOpen}>
        <Flex
          as='nav'
          direction='column'
          h='100%'
          justifyContent='space-between'
          position='relative'
        >
          <NavToggle
            aria-label='Toggle Sidebar'
            bg={`sidebar.icon.${colorScheme}`}
            borderRadius='full'
            boxShadow='0px 0px 4px rgba(0, 0, 0, 0.5)'
            data-testid='sidebar_expand_toggle'
            icon={
              <Icon
                as={
                  isSideNavOpen ? HiOutlineChevronLeft : HiOutlineChevronRight
                }
                boxSize='16px'
                color={`sidebar.icon-text.${colorScheme}`}
              />
            }
            onClick={() => dispatch(toggleSideNavOpen())}
            size='xs'
          />
          <Grid h='full' templateRows='60px auto'>
            <Box>
              <Flex
                alignItems='center'
                as={Link}
                cursor='pointer'
                h='60px'
                href={GLOBAL}
                overflow='hidden'
                px='13px'
              >
                {isSideNavOpen ? (
                  <Image
                    alt='HiveWatch'
                    h='34px'
                    src='/FullLogo-White.svg'
                    w='149px'
                  />
                ) : (
                  <Image
                    alt='HiveWatch'
                    h='34px'
                    src='/Icon-White.svg'
                    w='auto'
                  />
                )}
              </Flex>
            </Box>
            <Flex
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                  width: 0,
                },
              }}
              flexDirection='column'
              justifyContent='space-between'
              overflowY='auto'
              pt='20px'
            >
              <Box>
                {!isIPv6RedirectionEnabled && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_global'
                    icon={GlobeIcon}
                    label='Global'
                    path={GLOBAL}
                  />
                )}
                <SideBarNavItem
                  colorScheme={colorScheme}
                  dataTestId='sidebar_navItem_facilities'
                  icon={FacilityIcon}
                  label='Facilities'
                  path={FACILITIES}
                />
                <SideBarNavItem
                  colorScheme={colorScheme}
                  dataTestId='sidebar_navItem_incidents'
                  icon={IncidentIcon}
                  label='Incidents'
                  path={INCIDENTS}
                />
                {!hasNoLiveStream && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_devices'
                    icon={SecurityDeviceIcon}
                    label='Devices'
                    path={DEVICES}
                  />
                )}

                {shouldShowOperatorHubPage && (
                  <SideBarNavItem
                    badge={
                      shouldShowOperatorHubBadge ? <NewBadge /> : undefined
                    }
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_aiOperatorHub'
                    icon={AiOperatorIcon}
                    label='AI Operator Hub'
                    onClick={onOperatorHubClick}
                    path={AI_OPERATOR_HUB}
                  />
                )}

                {shouldShowDashboardPage &&
                  (shouldShowLegacyDashboard ? (
                    <SideBarNavItem
                      colorScheme={colorScheme}
                      dataTestId='sidebar_navItem_metrics'
                      icon={DashboardIcon}
                      label='Metrics'
                      path={METRICS_DASHBOARD}
                    />
                  ) : (
                    <SideBarNavItem
                      colorScheme={colorScheme}
                      dataTestId='sidebar_navItem_dashboard'
                      icon={BiTrendingDown}
                      label='Dashboard'
                      path={DASHBOARD}
                    />
                  ))}
                <SideBarNavItem
                  activePaths={[CREATE_INCIDENT_RESPONSE_WORKFLOW]}
                  colorScheme={colorScheme}
                  dataTestId='sidebar_navItem_sop'
                  icon={MdFormatListBulleted}
                  label='SOPs'
                  path={STANDARD_OPERATING_PROCEDURES}
                />
                {isEmergencyCallEnabled && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_emergencyCalling'
                    icon={EmergencyCallIcon}
                    label='Emergency Calls'
                    path={EMERGENCY_CALL_LOG}
                  />
                )}
                {isGuardToursEnabled && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_guardTours'
                    icon={GuardTourIcon}
                    label='Guard Tours'
                    onClick={() =>
                      mixpanel.track('Button Press: Nav - Guard Tours List')
                    }
                    path={GUARD_TOURS}
                  />
                )}
                {isOrangePeel && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_gateways'
                    icon={GatewayIcon}
                    label='Gateways'
                    path={GATEWAYS}
                  />
                )}
                {isCaseManagementEnabled && shouldShowCaseManagementPage && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_case_management'
                    icon={CaseManagementIcon}
                    label='Case Management'
                    path={CASE_MANAGEMENT}
                  />
                )}

                {isIncidentRulesEnabled && shouldShowIncidentRulesPage && (
                  <SideBarNavItem
                    colorScheme={colorScheme}
                    dataTestId='sidebar_navItem_incidentRules'
                    icon={RuleIcon}
                    iconSize='32px'
                    label='Rules'
                    path={INCIDENT_RULES}
                  />
                )}
                {isRecommendationsEngineEnabled &&
                  shouldShowProgramRecommendationsPage && (
                    <SideBarNavItem
                      badge={
                        pendingRecommendationsCount > 0 ? (
                          <BadgeCounter
                            colorScheme={colorScheme}
                            count={pendingRecommendationsCount}
                            variant='navsm'
                          />
                        ) : undefined
                      }
                      colorScheme={colorScheme}
                      dataTestId='sidebar_navItem_programRecommendations'
                      icon={ProgramRecommendationsIcon}
                      label='Recommendations'
                      path={PROGRAM_RECOMMENDATIONS}
                    />
                  )}
              </Box>
              <Box>
                {!isRemoveEditModeEnabled && shouldShowEditMode && (
                  <SideBarEditMode />
                )}
                <SideBarProfile />
              </Box>
            </Flex>
          </Grid>
        </Flex>
      </SideNavContainer>
    </DarkProvider>
  )
}
